import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ThemeToggleProps {
  /** "compact" = icon-only segmented (nav), "full" = with labels (settings) */
  variant?: "compact" | "full";
  className?: string;
}

const OPTIONS = [
  { value: "light", icon: Sun, key: "theme.light" },
  { value: "system", icon: Monitor, key: "theme.system" },
  { value: "dark", icon: Moon, key: "theme.dark" },
] as const;

export function ThemeToggle({ variant = "compact", className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  // Avoid hydration mismatch — render an inert placeholder until mounted
  useEffect(() => setMounted(true), []);

  const current = mounted ? (theme ?? "system") : "system";

  return (
    <div
      role="radiogroup"
      aria-label={t("theme.label", "Theme")}
      className={cn(
        "inline-flex items-center rounded-full border border-border bg-card/60 backdrop-blur-md p-0.5 shadow-glass-inset",
        className
      )}
    >
      {OPTIONS.map(({ value, icon: Icon, key }) => {
        const active = current === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={t(key, value)}
            title={t(key, value)}
            onClick={() => setTheme(value)}
            className={cn(
              "no-min-tap relative flex items-center justify-center rounded-full transition-all duration-200",
              variant === "compact" ? "h-7 w-7" : "h-9 px-3 gap-2 text-sm",
              active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-foreground/60 hover:text-foreground hover:bg-foreground/5"
            )}
          >
            <Icon className={variant === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden="true" />
            {variant === "full" && <span>{t(key, value)}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Single icon-button theme toggle for dense headers (light ↔ dark).
 * Use this inside glass control clusters where the segmented pill would nest.
 * Settings/profile pages keep the full 3-state ThemeToggle.
 */
export function ThemeToggleIconButton({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === "dark";
  const Icon = isDark ? Sun : Moon;
  const next = isDark ? "light" : "dark";

  return (
    <button
      type="button"
      aria-label={t("theme.toggle", "Toggle theme")}
      title={t("theme.toggle", "Toggle theme")}
      onClick={() => setTheme(next)}
      className={cn(
        "no-min-tap inline-flex items-center justify-center h-7 w-7 rounded-full",
        "text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export default ThemeToggle;
